import { useCallback, useMemo } from 'react';
import {
  HOME_LAYOUT_KEY,
  HOME_WIDGETS,
  UNDO_OFFER_MS,
  isWidgetScreen,
  shownColumns,
  widgetChoices,
  widgetLayoutKey,
  withWidget,
  type LayoutColumn,
} from '@/domain';
import { useSession } from '@/application/usecases/useSession';
import { useArrangement } from '@/features/dashboard/useArrangement';
import { strings } from '@/shared/strings';
import { toast } from '@/ui/primitives/toastStore';

export interface HomeNotificationsChoice {
  /**
   * Whether the choice may be acted on yet: at once for anybody this device has
   * not seen arrange their home screen, and for somebody it has, once their
   * account has answered. See `useArrangement`.
   */
  ready: boolean;
  /**
   * The person's home is not a screen of widgets, so the shell draws the
   * section over it. On a Dashboard or the Overview the section is a widget
   * and the screen draws it.
   */
  plain: boolean;
  /** Whether the section is on their home screen. Yes, until they take it off. */
  shown: boolean;
  set: (shown: boolean) => void;
  /** Takes it off, and says so with the way to put it back. */
  hide: () => void;
}

/**
 * Whether a person's latest notifications are on their home screen, and the
 * way to take them off and put them back. The lab's answer of 12 October 2026:
 * "make the notification on the home screen stay, but also removed. it is
 * actually good UX if they want it".
 *
 * One choice, wherever their home is. On a Dashboard or the Overview it is the
 * notifications widget of that screen, the same one Customize ticks. On any
 * other home, a technician's queue among them, it is the one thing the shell
 * draws over the screen, kept the same way under its own key. So the control
 * on the section itself, the tick in Customize and the switch in a person's
 * notification settings all read and write the one thing.
 */
export function useHomeNotifications(): HomeNotificationsChoice {
  const { data: session } = useSession();
  const home = session?.homeView;
  const widgetScreen = isWidgetScreen(home) ? home : null;

  const views = session?.views;
  const permissions = session?.permissions;
  const choices = useMemo<readonly LayoutColumn[]>(
    () =>
      widgetScreen === null
        ? HOME_WIDGETS
        : widgetChoices(widgetScreen, { views: views ?? [], permissions: permissions ?? [] }),
    [widgetScreen, views, permissions],
  );

  const { layout, save, settled } = useArrangement(
    widgetScreen === null ? HOME_LAYOUT_KEY : widgetLayoutKey(widgetScreen),
  );

  const set = useCallback(
    (shown: boolean) => save(withWidget(choices, layout, 'notifications', shown)),
    [choices, layout, save],
  );

  const hide = useCallback(() => {
    set(false);
    toast(strings.notifications.home.hidden, {
      durationMs: UNDO_OFFER_MS,
      action: { label: strings.notifications.home.putBack, onClick: () => set(true) },
    });
  }, [set]);

  return {
    ready: settled,
    plain: widgetScreen === null,
    shown: shownColumns(choices, layout).includes('notifications'),
    set,
    hide,
  };
}
